import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import moment from 'moment-timezone';
import I18n from '@/modules/i18n';
import { LTI_KINDS } from '@/components/shared/Lms/lmsProviders';
import { validDate } from '../../../common/Forms/Validators';
import Section from '../Section';
import sectionStyles from '../Section.module.scss';
import Tooltip from '../../../common/Tooltip/index';
import { assignmentInitialValuesPropType } from '../PropTypes';
import useAssignablesStore from '../WhatSection/store/useAssignablesStore';
import DateTimePicker from './DateTimePicker';
import styles from './WhenSection.module.scss';

export default class WhenSection extends Component {
  MODEL_TYPES = {
    classroomActivity: 'Classroom Activities',
    investigationActivity: 'Investigation Activities',
    lessonOutline: 'Lesson Outlines',
    readingActivity: 'Reading Activities',
    unitProjects: 'Unit Projects',
    videoActivity: 'Video Activities',
  };

  static propTypes = {
    form: PropTypes.shape({
      getState: PropTypes.func.isRequired,
    }).isRequired,
    formHandleSelect: PropTypes.func.isRequired,
    games: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        score: PropTypes.number,
        value: PropTypes.string,
      })
    ).isRequired,
    initialValues: assignmentInitialValuesPropType,
    libraryReadingTools: PropTypes.bool,
    ltiKind: PropTypes.oneOf(LTI_KINDS),
    modelType: PropTypes.string,
    newRecord: PropTypes.bool,
    showWhenTitle: PropTypes.bool,
  };

  static defaultProps = {
    initialValues: {},
    libraryReadingTools: false,
    ltiKind: null,
    modelType: '',
    newRecord: true,
    showWhenTitle: true
  };

  constructor(props) {
    super(props);

    this.state = {
      libraryTypes: useAssignablesStore.getState().libraryTypes,
      modelType: this.props.initialValues.modelType,
      modelsToAssign: this.props.form.getState().values.modelsToAssign,
      showFutureDateWarning: false,
      showStartDatePicker: !this.props.newRecord,
    };

    this.handleEnforceDatesChange = this.handleEnforceDatesChange.bind(this);
    this.setModelType = this.setModelType.bind(this);
    this.setModelsToAssign = this.setModelsToAssign.bind(this);
    this.setShowFutureDateWarning = this.setShowFutureDateWarning.bind(this);
    this.showStartDatePicker = this.showStartDatePicker.bind(this);
  }

  // AIDEV-NOTE: this is a class component, so it can't use the zustand hook — but getState() alone reads
  // the store outside React's subscription and leaves the section stale. The type list resolves after the
  // first render, so editing an existing library assignment would otherwise render with an empty
  // libraryTypes and show Enforce Due Date as available. Subscribing keeps the mirrored copy honest.
  componentDidMount() {
    this.unsubscribeStore = useAssignablesStore.subscribe(({ libraryTypes }) => {
      if (libraryTypes !== this.state.libraryTypes) this.setState({ libraryTypes });
    });
  }

  componentDidUpdate(prevProps) {
    // If modelType passed is not the same as the current state, i.e. the type has just been changed.
    const modelTypeHasChanged = this.props.modelType !== this.state.modelType;
    const modelsToAssignHasChanged = this.props.form.getState().values.modelsToAssign !== this.state.modelsToAssign;

    if (modelTypeHasChanged || (modelsToAssignHasChanged && this.props.form.getState().values.modelsToAssign?.length)) {
      if (this.isDisabled()) {
        // Uncheck "Enforce Due Date" box.
        this.props.formHandleSelect(false, 'dates_enforced');
      }
      this.setModelType();
      this.setModelsToAssign();
    }

    const oldStartTime = prevProps.initialValues.start_datetime;
    const newStartTime = this.props.initialValues.start_datetime;

    if (newStartTime && (newStartTime !== oldStartTime)) {
      this.setShowFutureDateWarning({ value: this.props.initialValues.start_datetime });
    }
  }

  componentWillUnmount() {
    this.unsubscribeStore?.();
  }

  handleEnforceDatesChange(clickEvent) {
    this.props.formHandleSelect(clickEvent.target.value, 'dates_enforced');
  }

  setModelType() {
    this.setState({ modelType: this.props.modelType });
  }

  setModelsToAssign() {
    this.setState({ modelsToAssign: this.props.form.getState().values.modelsToAssign });
  }

  setShowFutureDateWarning(datetime) {
    const showFutureDateWarning = moment.utc(datetime.value, 'YYYY-MM-DD HH:mm:ss').isAfter();

    this.setState({ showFutureDateWarning });
  }

  showStartDatePicker() {
    this.setState({ showStartDatePicker: true });
  }

  renderStartDateField() {
    if (this.state.showStartDatePicker) {
      return (
        <div>
          {this.state.showFutureDateWarning && (
            <div className={styles.notice}>
              <i aria-hidden="true" className={`fa fa-warning ${styles.warning}`} />
              {' '}
              <span>Students will not have access to the assigned content until the available date/time.</span>
            </div>
          )}
          <DateTimePicker
            callback={this.setShowFutureDateWarning}
            fieldName="start_datetime"
            formHandleSelect={this.props.formHandleSelect}
            initialValues={this.props.initialValues}
            label="Available Date"
            required={false}
            timeBuffer={0}
            validator={validDate}
          />
        </div>
      );
    }

    return (
      <div className={`${sectionStyles.fieldContainer} mt10 mb15`}>
        <div className={sectionStyles.label}>
          Available Date
        </div>

        <div className={styles.pickerContainer}>
          Immediately
          <button
            className={`btn btn--sm btn--link-purple ${styles.editStartDate}`}
            type="button"
            onClick={this.showStartDatePicker}
          >
            <i aria-label="Edit available date" className="fa fa-pencil-square-o" />
            &nbsp;
            Schedule for Later
          </button>
        </div>
      </div>
    );
  }

  isVocabGame() {
    if (this.modelsToAssign?.length && this.props.games?.length) return false;

    const modelToAssign = this.props.form.getState().values.modelsToAssign;
    const label = this.props.games?.find(game => game.value.includes(modelToAssign))?.label;

    return label?.includes('Vocabulary');
  }

  isLibrary() {
    return this.state.libraryTypes.includes(this.state.modelType);
  }

  isDisabled() {
    // AIDEV-NOTE: libraries are checked separately — which types are libraries is content data, not a
    // fixed list. They support Enforce Due Date only behind library_reading_tools (PLAT-17824).
    if (this.isLibrary()) return !this.props.libraryReadingTools;

    switch (this.state.modelType) {
      case 'classroomActivity':
      case 'investigationActivity':
      case 'lessonOutline':
      case 'readingActivity':
      case 'unitProjects':
      case 'videoActivity':
        return true;
      case 'games':
        return this.isVocabGame();
      default:
        return false;
    }
  }

  hoverMessage() {
    if (!this.isDisabled()) return '';

    if (this.isVocabGame()) return 'Enforce Due Date is not available for Vocabulary Games';

    const modelLabel = this.isLibrary() ? 'Libraries' : this.MODEL_TYPES[this.state.modelType];

    return `Enforce Due Date is not available for ${modelLabel}`;
  }

  renderEnforceDates() {
    return (
      <div className={`${sectionStyles.fieldContainer} mt5`}>
        <div className={`${sectionStyles.spacer} inline_block`} />
        <Tooltip
          content={this.hoverMessage()}
          shouldDisplay={this.isDisabled()}
        >
          <div>
            <Field
              className="ml0"
              component="input"
              defaultChecked={this.props.initialValues.dates_enforced}
              disabled={this.isDisabled()}
              id="enforce-dates"
              name="dates_enforced"
              type="checkbox"
              onClick={this.handleEnforceDatesChange}
            />
          </div>
        </Tooltip>
        <label htmlFor="enforce-dates">
          <i aria-hidden="true" className="fa fa-lock" />
          {' Enforce Due Date'}
          <Tooltip
            content="Students cannot submit assignment after due date."
          >
            <span>
              <i aria-hidden="true" className="fa fa-question-circle ml5" />
              <span className="sr-only">More information</span>
            </span>
          </Tooltip>
        </label>
      </div>
    );
  }

  renderLtiTooltip() {
    if (!this.props.ltiKind) return null;

    const content = I18n.t(`due_date_sync_${this.props.ltiKind}`);

    return (
      <Tooltip content={content}>
        <span>
          <i aria-hidden="true" className="fa fa-question-circle ml5" />
          <span className="sr-only">More information</span>
        </span>
      </Tooltip>
    );
  }

  render() {
    if (this.props.showWhenTitle) {
      return (
        <Section title="When">
          {this.renderStartDateField()}

          <DateTimePicker
            fieldName="due_datetime"
            formHandleSelect={this.props.formHandleSelect}
            initialValues={this.props.initialValues}
            label="Due Date"
            tooltip={this.renderLtiTooltip()}
            validator={validDate}
          />

          <div className={`${sectionStyles.timeZone}`}>
            <div className="inline_block">Timezone: {moment.tz(moment.tz.guess()).zoneAbbr()}</div>
          </div>

          {this.renderEnforceDates()}
        </Section>
      );
    }

    return (
      <>
        <DateTimePicker
          fieldName="due_datetime"
          formHandleSelect={this.props.formHandleSelect}
          label="Due Date"
          tooltip={this.renderLtiTooltip()}
          validator={validDate}
        />

        <div className={`${sectionStyles.timeZone}`}>
          <div className="inline_block">Timezone: {moment.tz(moment.tz.guess()).zoneAbbr()}</div>
        </div>

        {this.renderEnforceDates()}
      </>
    );
  }
}
